import { InputRule } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';

/**
 * Matches a "[]" or "[ ]" checkbox marker at the very start of the title (optionally after
 * leading whitespace, mirroring the standard task checkbox shortcut), triggered by the space
 * typed after the closing bracket.
 */
const TITLE_TO_TASK_FIND = /^\s*\[ ?\] $/;

/**
 * Input rule that converts a title beginning with a "[]"/"[ ]" marker into a task item at the
 * start of the task list, carrying across any text that followed the marker, and placing the
 * cursor at the start of the new task.
 */
export const createTitleToTaskInputRule = (titleTypeName: string): InputRule =>
    new InputRule({
        find: TITLE_TO_TASK_FIND,
        handler: ({ state, range }) => {
            const { tr, schema, doc, selection } = state;

            // Only convert when typing within the title node.
            if (selection.$from.parent.type.name !== titleTypeName) return null;

            const titleNode = doc.firstChild;
            const titleType = schema.nodes[titleTypeName];
            const taskItemType = schema.nodes[TiptapNodeType.taskItem];
            const paragraphType = schema.nodes[TiptapNodeType.paragraph];

            if (!taskItemType || !paragraphType || titleNode?.type !== titleType) return null;

            // The title content after the matched marker becomes the new task's text. The trigger
            // space isn't in the document yet, so `range.to` already sits just after the marker.
            const titleContentEnd = titleNode.nodeSize - 1;
            const remainingContent = doc.slice(range.to, titleContentEnd).content;

            const taskItem = taskItemType.create({ checked: false }, paragraphType.create(null, remainingContent));

            // Replace the whole title with the new task as the first item of the task list (the task
            // list becomes the first node after the title is removed, so its content starts at 1).
            tr.delete(0, titleNode.nodeSize);
            tr.insert(1, taskItem);

            // Place the cursor at the start of the new task's text (inside its paragraph).
            tr.setSelection(TextSelection.near(tr.doc.resolve(3)));

            return undefined;
        },
    });

/** Matches a "#" heading marker typed at the start of a task, triggered by the following space. */
const TASK_TO_TITLE_FIND = /^# $/;

/**
 * Input rule that converts an empty first task into a title when "# " is typed in it (and no title
 * exists yet). The cursor lands in the new title. When the task is the only item it is kept (so the
 * list stays valid) but emptied; when there are other items the empty first task is removed.
 */
export const createTaskToTitleInputRule = (): InputRule =>
    new InputRule({
        find: TASK_TO_TITLE_FIND,
        handler: ({ state, range }) => {
            const { tr, schema, doc, selection } = state;
            const { $from } = selection;

            const titleType = schema.nodes[TiptapNodeType.taskListTitle];

            if (!titleType) return null;

            // Don't add a second title.
            if (doc.firstChild?.type === titleType) return null;

            // Must be the first top-level task's paragraph (doc > taskList > taskItem > paragraph).
            const taskItemDepth = $from.depth - 1;
            const parentListDepth = $from.depth - 2;

            if (parentListDepth !== 1) return null;
            if ($from.index(parentListDepth) !== 0) return null;
            if ($from.parent.type.name !== TiptapNodeType.paragraph) return null;

            const taskItem = $from.node(taskItemDepth);

            if (taskItem.type.name !== TiptapNodeType.taskItem) return null;

            // Only convert an empty task: just the paragraph (no nested children) holding only "#".
            if (taskItem.childCount !== 1) return null;
            if ($from.parent.textContent !== '#') return null;

            const titleNode = titleType.createAndFill();

            if (!titleNode) return null;

            const parentList = $from.node(parentListDepth);

            if (parentList.childCount > 1) {
                // Remove the whole empty first task; the remaining items keep the list valid.
                const taskItemStart = $from.before(taskItemDepth);
                tr.delete(taskItemStart, taskItemStart + taskItem.nodeSize);
            } else {
                // Keep the only task (so the list never empties) but clear the typed "#".
                tr.delete(range.from, range.to);
            }

            // Insert the new empty title at the start of the document, with the cursor inside it.
            tr.insert(0, titleNode);
            tr.setSelection(TextSelection.near(tr.doc.resolve(1)));

            return undefined;
        },
    });
